import type { SectionSaver } from '../../contexts/SpaceSettingsContext';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import useSelectedSpace from '../../hooks/useSelectedSpace';

import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import SpaceSettingsSaveContext from '../../contexts/SpaceSettingsContext';

import PageLayout from '../../layout/PageLayout/PageLayout';
import ContentPreviewSettings from './ContentPreviewSettings/ContentPreviewSettings';
import CustomLinksSettings from './CustomLinksSettings/CustomLinksSettings';
import FormCollaborationSettings from './FormCollaborationSettings/FormCollaborationSettings';
import ManageDataSettings from './ManageDataSettings/ManageDataSettings';

const SpaceSettings = () => {
  const { t } = useTranslation();
  const { buildUrlWithSpace, spaceData } = useSelectedSpace();
  const { setDirty } = use(DirtyHandlerContext);

  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});

  const setSectionDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyMap((prev) =>
      prev[id] === dirty ? prev : { ...prev, [id]: dirty },
    );
  }, []);

  const anyDirty = useMemo(
    () => Object.values(dirtyMap).some(Boolean),
    [dirtyMap],
  );

  useEffect(() => {
    setDirty(anyDirty);
    return () => setDirty(false);
  }, [anyDirty, setDirty]);

  const saversRef = useRef<Map<string, SectionSaver>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const register = useCallback((saver: SectionSaver) => {
    saversRef.current.set(saver.id, saver);
  }, []);

  const unregister = useCallback((id: string) => {
    saversRef.current.delete(id);
  }, []);

  const saveContextValue = useMemo(
    () => ({ register, unregister, setSectionDirty }),
    [register, unregister, setSectionDirty],
  );

  const handleSaveAll = useCallback(async () => {
    const dirty = [...saversRef.current.values()].filter((s) => s.isDirty());
    if (dirty.length === 0) return;

    setIsSaving(true);
    try {
      const results = await Promise.allSettled(dirty.map(({ save }) => save()));
      results
        .filter((r) => r.status === 'rejected')
        .forEach(() => toast.error(t('Form.CommunicationErrorMessage')));
    } finally {
      setIsSaving(false);
    }
  }, [t]);

  return (
    <PageLayout
      page="spaceSettings"
      title={t('Global.SpaceSettings')}
      buttons={
        <>
          <TopbarCancelButton
            key="cancel_button"
            link={buildUrlWithSpace('')}
          />
          <TopbarSaveButton
            key="save_button"
            onClick={handleSaveAll}
            isLoading={isSaving}
            disabled={!anyDirty || isSaving}
          />
        </>
      }
      testId={'space-settings'}
    >
      <SpaceSettingsSaveContext value={saveContextValue}>
        <div className="flex flex-col gap-4">
          <FormCollaborationSettings spaceName={spaceData?.name || ''} />
          <ContentPreviewSettings contentTypeName="_content_preview" />
          <CustomLinksSettings contentTypeName="_custom_links" />
          <ManageDataSettings spaceId={spaceData?.id} />
        </div>
      </SpaceSettingsSaveContext>
    </PageLayout>
  );
};

export default SpaceSettings;
